import { inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormlyFieldConfig } from '@ngx-formly/core';
import { catchError, EMPTY, finalize, forkJoin, map, of, switchMap, take, type Observable } from 'rxjs';

import { EntriesService } from 'app/features/admin/modules/features/entries/entries.service';
import { InterfaceActionSettled } from 'app/features/admin/modules/features/interface-actions/interface-action-settled';
import type { InterfaceActionSettledEvent } from 'app/features/admin/modules/features/interface-actions/interface-action-settled.types';
import type { InterfaceAction } from 'app/features/admin/modules/features/interface-actions/interface-actions.model';
import { InterfaceActionsService } from 'app/features/admin/modules/features/interface-actions/interface-actions.service';
import type { Entry, Module } from 'app/features/admin/modules/modules.model';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { findInFieldTree } from '../components/formly-fields/field-tree.util';
import { ReviewChecklistTemplatesService } from '../components/formly-fields/review-checklist-templates.service';
import { ReviewDocumentsQueryService } from '../components/formly-fields/review-documents-query.service';
import type { ReviewFlag } from '../components/review-flag/review-flags.types';
import { openFlags, readFlags } from '../components/review-flag/review-flags.util';
import { documentBelongsToReview, reviewDocumentsFilter } from '../utils/onboarding-checklist-templates';
import { UtilityService } from './utility.service';
import type { SettledReviewDecision } from './review-decision-coordinator.types';

const DOCUMENT_MODULE_CODE = 'document-records';
const DOCUMENT_PAGE_SIZE = 50;
const DENIED_STATUS = 'afgekeurd';
const PROCESSING_STATUS = 'verwerken';
const REVIEWING_STATUS = 'in-behandeling';
const READONLY_ONBOARDING_FIELD_TYPE = 'readonly_onboarding';
/** The review field's key when a configured field carries none of its own (as the panel renders it). */
const DEFAULT_REVIEW_FIELD_KEY = 'review';

type ReviewDecision = 'approved' | 'rejected';
type TransitionOutcome = 'ok' | 'failed' | 'unsettled';

/**
 * Finds the `readonly_onboarding` field anywhere in a generated form, including inside nested
 * `fieldGroup`s (wizard pages, tabs). There is exactly one per module in practice, so the first
 * match wins. See `field-tree.util.ts` for the shared walk.
 */
export function findReadonlyOnboardingField(fields: FormlyFieldConfig[] | undefined): FormlyFieldConfig | undefined {
  return findInFieldTree(fields, field => field.type === READONLY_ONBOARDING_FIELD_TYPE);
}

/**
 * Completes a review decision after the parent interface action has settled.
 *
 * It listens to `InterfaceActionSettled`, which `EntriesService.processAction` publishes for every
 * single-entry action, so the decision moves its documents whichever screen it was taken on — the
 * aanmelding's detail page and the review inbox alike. No screen calls this service itself.
 *
 * This coordinator is root-owned deliberately: once the reviewer confirms the header action,
 * reading the server-confirmed parent and updating its documents must survive closing the panel
 * or navigating away. The component that initiated the action is therefore not part of this
 * subscription's lifecycle. It is created at bootstrap (`app.config.ts`) so it is subscribed before
 * the first action can settle.
 *
 * Bulk runs do not pass through `processAction` and are not covered (see `InterfaceActionSettled`).
 */
@Injectable({ providedIn: 'root' })
export class ReviewDecisionCoordinator {
  // Resolved on first use, not at construction: this service is created at bootstrap, also on the
  // signed-out shell, and constructing ModulesStore (loads modules) or EntriesService (opens the
  // SignalR hub) there would start network work before anyone is signed in. By the time an action
  // settles, the pipeline that published it has created all of them anyway.
  readonly #injector = inject(Injector);
  readonly #activeApplications = new Set<string>();

  constructor() {
    inject(InterfaceActionSettled)
      .events$.pipe(takeUntilDestroyed())
      .subscribe(event => this.onSettled(event));
  }

  get #modulesStore() {
    return this.#injector.get(ModulesStore);
  }
  get #entriesService() {
    return this.#injector.get(EntriesService);
  }
  get #documentsQuery() {
    return this.#injector.get(ReviewDocumentsQueryService);
  }
  get #checklistTemplates() {
    return this.#injector.get(ReviewChecklistTemplatesService);
  }
  get #interfaceActions() {
    return this.#injector.get(InterfaceActionsService);
  }
  get #utilityService() {
    return this.#injector.get(UtilityService);
  }

  /**
   * Turns a settled action from any surface into a review decision request, or ignores it.
   *
   * Cheap checks first, so that only a possible decision costs the GET `handleSettled` starts
   * with: the action must have an `editValue` step on the acting module that succeeded. A navigate
   * such as "Beoordeel aanmelding", a sync onto another module, or a cancelled popup (no outcomes
   * at all) cannot be a review decision. Outcomes line up with the action's steps by index
   * (`executeActions` forkJoins them in order). A module whose generated form has no
   * `readonly_onboarding` field has no review to complete. Every other gate — any failed or
   * unsettled step, the pre-write status, one chain per aanmelding — stays in `handleSettled`.
   */
  onSettled(event: InterfaceActionSettledEvent): void {
    const editedActingModule = (event.action.actions ?? []).some(
      (step, index) =>
        step?.type === 'editValue' &&
        step.moduleCode === event.entity.code &&
        event.outcomes[index]?.status === 'succeeded'
    );
    if (!editedActingModule) return;

    const reviewField = findReadonlyOnboardingField(this.#modulesStore.generatedForms()[event.entity.id]);
    if (!reviewField) return;

    const props = reviewField.props ?? {};
    this.handleSettled({
      application: event.entry.moduleId ? event.entry : { ...event.entry, moduleId: event.entity.id },
      outcomes: event.outcomes,
      reviewFieldKey: typeof reviewField.key === 'string' ? reviewField.key : DEFAULT_REVIEW_FIELD_KEY,
      reviewApproveActionId: props['reviewApproveActionId'] as string | undefined,
      reviewRejectActionId: props['reviewRejectActionId'] as string | undefined,
    });
  }

  handleSettled(request: SettledReviewDecision): void {
    if (!request.application.id || !request.application.moduleId) return;
    if (request.outcomes.some(outcome => outcome.status === 'failed' || outcome.status === 'unsettled')) return;
    // Only a decision action that started from the review state may fan out. This rejects a
    // cancelled/no-op or unrelated action clicked on an already decided row.
    if (request.application.entry?.['status']?.slug !== REVIEWING_STATUS) return;
    if (this.#activeApplications.has(request.application.id)) return;
    this.#activeApplications.add(request.application.id);

    this.#entriesService
      .getEntry(request.application.moduleId, request.application.id)
      .pipe(
        take(1),
        switchMap(confirmed => this.#transitionConfirmedDecision(confirmed, request)),
        catchError(error => {
          console.error('Bevestigde onboardingbeslissing verwerken mislukt:', error);
          this.#utilityService.notify('De bevestigde beoordeling kon niet worden verwerkt; documenten zijn niet bijgewerkt.');
          return EMPTY;
        }),
        finalize(() => this.#activeApplications.delete(request.application.id))
      )
      .subscribe();
  }

  #transitionConfirmedDecision(confirmed: Entry, request: SettledReviewDecision): Observable<unknown> {
    const status = confirmed.entry?.['status']?.slug;
    const decision: ReviewDecision | null =
      status === DENIED_STATUS ? 'rejected' : status === PROCESSING_STATUS ? 'approved' : null;
    if (!decision) return EMPTY;

    const documentModule = this.#modulesStore
      .enabledEntities()
      .find((module: Module) => module.code === DOCUMENT_MODULE_CODE);
    if (!documentModule?.id) {
      this.#utilityService.notify('De documentenmodule is niet beschikbaar; documenten zijn niet bijgewerkt.');
      return EMPTY;
    }

    const actionId = decision === 'approved' ? request.reviewApproveActionId : request.reviewRejectActionId;
    const action = actionId ? documentModule.interfaceActions?.find(candidate => candidate.id === actionId) : undefined;
    if (!action) {
      this.#notifyMissingAction(decision);
      return EMPTY;
    }

    const userId = confirmed.entry?.['user']?.id;
    if (typeof userId !== 'string' || !userId) {
      this.#utilityService.notify('De gebruiker van de beoordeling ontbreekt; documenten zijn niet bijgewerkt.');
      return EMPTY;
    }
    const companyId = confirmed.entry?.['company']?.id;
    const safeCompanyId = typeof companyId === 'string' && companyId ? companyId : null;
    const filter = reviewDocumentsFilter(userId, safeCompanyId);
    const decidedAt = this.#decisionTime(confirmed);
    if (decidedAt === null) {
      this.#utilityService.notify(
        'De bevestigde beoordeling heeft geen geldig beslismoment; documenten zijn niet bijgewerkt.'
      );
      return EMPTY;
    }

    const loadDocuments = () =>
      this.#documentsQuery.loadAll({ moduleId: documentModule.id, filter, pageSize: DOCUMENT_PAGE_SIZE });

    return forkJoin([loadDocuments(), this.#checklistTemplates.visibleTemplateIds(confirmed.entry?.['onboarding-form'])]).pipe(
      map(([documents, templateIds]) =>
        documents.filter(document => documentBelongsToReview(document, userId, safeCompanyId, templateIds))
      ),
      switchMap(documents =>
        this.#executeDocumentTransitions(
          action,
          decision,
          documents,
          openFlags(readFlags(confirmed, request.reviewFieldKey)),
          decidedAt,
          loadDocuments
        )
      )
    );
  }

  #executeDocumentTransitions(
    action: InterfaceAction,
    decision: ReviewDecision,
    documents: readonly Entry[],
    flags: readonly ReviewFlag[],
    decidedAt: number,
    reloadDocuments: () => Observable<readonly Entry[]>
  ): Observable<unknown> {
    const executions: Observable<{ id: string; outcome: TransitionOutcome }>[] = [];
    for (const document of documents) {
      const id = document.id ?? '';
      const values = this.#transitionValues(document, decision, flags, decidedAt);
      if (!values) continue;
      try {
        executions.push(
          this.#interfaceActions.executeWithValues(action, document, values).pipe(
            map(result => {
              if (!result.visible || result.steps.some(step => step.status === 'failed')) return 'failed' as const;
              if (result.steps.some(step => step.status === 'unsettled')) return 'unsettled' as const;
              return 'ok' as const;
            }),
            catchError(error => {
              console.error('Documentbeslissing uitvoeren mislukt:', error);
              return of('failed' as const);
            }),
            map(outcome => ({ id, outcome }))
          )
        );
      } catch (error) {
        console.error('Documentbeslissing voorbereiden mislukt:', error);
        executions.push(of({ id, outcome: 'failed' as const }));
      }
    }

    if (executions.length === 0) return EMPTY;
    return forkJoin(executions).pipe(
      switchMap(results => this.#confirmLanded(results, decision, reloadDocuments)),
      map(outcomes => {
        const failed = outcomes.filter(outcome => outcome === 'failed').length;
        const unsettled = outcomes.filter(outcome => outcome === 'unsettled').length;
        if (failed > 0) {
          this.#utilityService.notify(
            decision === 'rejected'
              ? `${failed} gemarkeerd(e) document(en) kon(den) niet worden afgewezen.`
              : `${failed} document(en) kon(den) niet worden goedgekeurd.`
          );
        }
        if (unsettled > 0) {
          this.#utilityService.notify(`${unsettled} document(en) kon(den) niet worden bevestigd — controleer de status.`);
        }
        return outcomes;
      })
    );
  }

  /**
   * A step reporting no failure does not prove the document changed: an action with no steps, only
   * skipped steps (navigate, a dropped openCreateForm) or a write to another module settles clean
   * while the document stays as it was. Re-read once and hold each 'ok' to the status it should now
   * carry; one that does not carry it counts as failed. A failed re-read cannot confirm anything, so
   * those outcomes become 'unsettled' rather than silently 'ok'.
   */
  #confirmLanded(
    results: readonly { id: string; outcome: TransitionOutcome }[],
    decision: ReviewDecision,
    reloadDocuments: () => Observable<readonly Entry[]>
  ): Observable<TransitionOutcome[]> {
    const outcomes = results.map(result => result.outcome);
    if (!outcomes.includes('ok')) return of(outcomes);
    return reloadDocuments().pipe(
      map(documents => {
        const statusById = new Map(documents.map(document => [document.id, document.entry?.['status']?.slug]));
        return results.map(({ id, outcome }) =>
          outcome === 'ok' && !this.#carriesDecision(statusById.get(id), decision) ? ('failed' as const) : outcome
        );
      }),
      catchError(error => {
        console.error('Documentstatus na beslissing controleren mislukt:', error);
        return of(outcomes.map(outcome => (outcome === 'ok' ? ('unsettled' as const) : outcome)));
      })
    );
  }

  /** An approved document past its validity date may already read 'expired' — approval still landed. */
  #carriesDecision(status: unknown, decision: ReviewDecision): boolean {
    return status === decision || (decision === 'approved' && status === 'expired');
  }

  #transitionValues(
    document: Entry,
    decision: ReviewDecision,
    flags: readonly ReviewFlag[],
    decidedAt: number
  ): Record<string, unknown> | null {
    const status = document.entry?.['status']?.slug;
    if (status === decision) return null;

    const reviewedAt = Date.parse(String(document.entry?.['reviewed-at'] ?? ''));
    if (Number.isFinite(reviewedAt) && reviewedAt >= decidedAt) return null;

    if (decision === 'rejected') {
      const flag = flags.find(candidate => candidate.target === document.id);
      if (!flag || (status !== 'pending' && status !== 'approved' && status !== 'expired')) return null;
      return { 'entry.review-comment': flag.reason.trim() || flag.label };
    }

    if (status !== 'pending' || flags.some(flag => flag.target === document.id)) return null;
    return {};
  }

  #decisionTime(entry: Entry): number | null {
    const updatedAt = Date.parse(String(entry.updatedAt ?? ''));
    return Number.isFinite(updatedAt) ? updatedAt : null;
  }

  #notifyMissingAction(decision: ReviewDecision): void {
    this.#utilityService.notify(
      decision === 'rejected'
        ? 'Geen Afwijzen-actie geconfigureerd op de documentenmodule; documenten zijn niet bijgewerkt.'
        : 'Geen Goedkeuren-actie geconfigureerd op de documentenmodule; documenten zijn niet bijgewerkt.'
    );
  }
}
